import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from "react";
import { DndSortChangeEvent } from "../types";

export type PreviewRendererParams = {
  valid: boolean; // Indicates if the item being dragged is valid for the current drop target
};

export type PreviewRenderer = (params: PreviewRendererParams) => ReactNode;

export type DndSortContextValue = {
  previewRenderer: PreviewRenderer; // Function to render the preview of the item being dragged
  setPreviewRenderer: Dispatch<SetStateAction<PreviewRenderer>>; // Function to set the preview node
  onDrop: (event: DndSortChangeEvent) => void; // Optional callback function when an item is dropped
};

const DEFAULT_CONTEXT_VALUE: DndSortContextValue = {
  previewRenderer: () => null,
  setPreviewRenderer: () => () => null,
  onDrop: () => {
    return;
  },
};

const DndSortContext = createContext(DEFAULT_CONTEXT_VALUE);

/**
 * Our internal context provider for the DndSort system. Not to be confused with DndContext which is the `dnd-kit` context.
 * @param props
 * @param props.children The children components that will have access to the context
 */
export default function DndSortContextProvider({
  children,
  onDrop,
}: {
  children: ReactNode;
  onDrop: (event: DndSortChangeEvent) => void;
}) {
  const [previewRenderer, setPreviewRenderer] = useState(
    () => DEFAULT_CONTEXT_VALUE.previewRenderer,
  );

  return (
    <DndSortContext.Provider
      value={{
        previewRenderer,
        setPreviewRenderer,
        onDrop,
      }}
    >
      {children}
    </DndSortContext.Provider>
  );
}

export const useDndSortContext = () => {
  return useContext(DndSortContext);
};
